import { useQueryParams } from '@atlas-frontend/sdk';
import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table';
import { useCallback, useEffect, useState } from 'react';

type UseInitialDescriptionFilterOptions<TData> = {
  data: TData[];
  filterId: string;
  getDescriptionId: (row: TData) => number | null | undefined;
  getFilterValue: (row: TData) => unknown;
};

const normalizeFilterValue = (value: unknown) =>
  value == null ? '' : String(value);

const getDescriptionFilter = (filters: ColumnFiltersState, filterId: string) =>
  filters.find((filter) => filter.id === filterId);

const replaceDescriptionFilter = (
  filters: ColumnFiltersState,
  filterId: string,
  value: unknown,
) => {
  const otherFilters = filters.filter((filter) => filter.id !== filterId);
  return value == null
    ? otherFilters
    : [...otherFilters, { id: filterId, value }];
};

export const useDescriptionFilter = <TData>(
  options: UseInitialDescriptionFilterOptions<TData>,
) => {
  const { data, filterId, getDescriptionId, getFilterValue } = options;
  const [{ descriptionId: descriptionIdString }, setQueryParams] =
    useQueryParams<{ descriptionId?: number }>();
  const [filters, setFiltersState] = useState<ColumnFiltersState>([]);
  const descriptionId = descriptionIdString
    ? Number(descriptionIdString)
    : undefined;

  useEffect(() => {
    const descriptionRow = descriptionId
      ? data.find((row) => getDescriptionId(row) === descriptionId)
      : undefined;
    const nextDescriptionFilterValue = descriptionRow
      ? getFilterValue(descriptionRow)
      : undefined;

    setFiltersState((current) => {
      const currentDescriptionFilter = getDescriptionFilter(current, filterId);

      return normalizeFilterValue(currentDescriptionFilter?.value) ===
        normalizeFilterValue(nextDescriptionFilterValue)
        ? current
        : replaceDescriptionFilter(
            current,
            filterId,
            nextDescriptionFilterValue,
          );
    });
  }, [data, descriptionId, filterId, getDescriptionId, getFilterValue]);

  const setFilters: OnChangeFn<ColumnFiltersState> = useCallback(
    (updaterOrValue) => {
      setFiltersState((current) => {
        const next =
          updaterOrValue instanceof Function
            ? updaterOrValue(current)
            : updaterOrValue;

        const currentDescriptionFilter = getDescriptionFilter(
          current,
          filterId,
        );
        const nextDescriptionFilter = getDescriptionFilter(next, filterId);

        if (
          normalizeFilterValue(currentDescriptionFilter?.value) !==
          normalizeFilterValue(nextDescriptionFilter?.value)
        ) {
          const matchingRow = nextDescriptionFilter
            ? data.find(
                (row) =>
                  normalizeFilterValue(getFilterValue(row)) ===
                  normalizeFilterValue(nextDescriptionFilter.value),
              )
            : undefined;

          setQueryParams(
            {
              descriptionId: matchingRow
                ? (getDescriptionId(matchingRow) ?? undefined)
                : undefined,
            },
            { replace: true },
          );
        }

        return next;
      });
    },
    [data, filterId, getDescriptionId, getFilterValue, setQueryParams],
  );

  return { filters, setFilters };
};
